/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useMaterialUIController } from "context/ThemeContext";
import {
  Box,
  Card,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Badge,
  IconButton,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  useTheme,
  Divider,
} from "@mui/material";
import {
  Notifications as NotificationIcon,
  Event as EventIcon,
  Payment as PaymentIcon,
  AccessAlarms as ReminderIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  MarkEmailRead as MarkReadIcon,
  Refresh as RefreshIcon,
  ShoppingCart as PurchaseIcon,
  ExpandMore as ExpandMoreIcon,
  VisibilityOff as HideIcon,
  Launch as LaunchIcon,
  Videocam,
  ReportProblem,
  AccountBalanceWallet,
} from "@mui/icons-material";
import useHttp from "hooks/http.hook";
import { NotificationPriority, NotificationType } from "@malmur-technologies/medicalsystem-shared";
import { NotificationInterface } from "@malmur-technologies/medicalsystem-shared";
import { useSelector, useDispatch } from "react-redux";
import { notificationActions, selectNotificationCounts } from "store/slices/notification.slice";
import { RootState } from "store/store";
import { t } from "i18next";
import { CoreAPI_URLs } from "constants/http.const";
import { formatDateMessages } from "utils/date.utils";
import "styles/components/_notifications.scss";

/**
 * Props for NotificationWidget component with extensive customization options.
 */
interface NotificationWidgetProps {
  apiBaseUrl?: string;
  userToken?: string;
  refreshInterval?: number;
  maxNotifications?: number;
  showStats?: boolean;
  autoRefresh?: boolean;
  enableRedirect?: boolean;
  forceRefresh?: boolean;
  lastRefreshTime?: Date;
  preloadedNotifications?: NotificationInterface[];
  shouldFetchData?: boolean;
  onNotificationClick?: (notification: NotificationInterface) => void;
  onNotificationAction?: (action: string, notificationId: string) => void;
  onBeforeRedirect?: (notification: NotificationInterface) => boolean;
  onUnreadCountChange?: (count: number) => void;
  onRefreshComplete?: () => void;
}

/**
 * Comprehensive notification center widget with real-time updates.
 * 
 * Features:
 * - Real-time notification fetching with configurable refresh interval
 * - Notification type filtering (all, appointments, payments, reminders, etc.)
 * - Priority-based sorting and visual indicators
 * - Mark as read/unread functionality
 * - Bulk actions (mark all as read, hide notifications)
 * - Notification statistics display
 * - Auto-refresh with manual refresh option
 * - Navigation integration (click to open related pages)
 * - Unread count tracking synced to Redux
 * - Loading states and error handling
 * - Dark mode support
 * - Icon mapping by notification type:
 *   - Appointment: EventIcon
 *   - Payment: PaymentIcon
 *   - Reminder: ReminderIcon
 *   - Videocall: Videocam
 *   - Warning: WarningIcon/ReportProblem
 *   - Billing: AccountBalanceWallet
 * 
 * Notification Types Supported:
 * - APPOINTMENT_REMINDER, APPOINTMENT_CONFIRMED, APPOINTMENT_CANCELLED
 * - PAYMENT_RECEIVED, PAYMENT_DUE, PAYMENT_OVERDUE
 * - VIDEOCALL_INVITATION, VIDEOCALL_MISSED
 * - BILLING_ALERT, SYSTEM_ALERT, GENERAL
 * 
 * @param apiBaseUrl - Base URL for notification API (optional)
 * @param userToken - Authentication token (optional, uses Redux by default)
 * @param refreshInterval - Auto-refresh interval in milliseconds (default: 10s)
 * @param maxNotifications - Maximum notifications to display (default: 50)
 * @param showStats - Display notification statistics (default: false)
 * @param autoRefresh - Enable automatic refresh (default: false)
 * @param enableRedirect - Enable navigation on click (default: true)
 * @param forceRefresh - Trigger immediate refresh (default: false)
 * @param shouldFetchData - Enable API fetching (default: true)
 * @param onNotificationClick - Custom click handler
 * @param onUnreadCountChange - Callback when unread count changes
 * @returns Notification widget component
 */
const NotificationWidget: React.FC<NotificationWidgetProps> = ({
  userToken,
  refreshInterval = 10000,
  maxNotifications = 50,
  showStats = false,
  autoRefresh = false,
  enableRedirect = true,
  forceRefresh = false,
  lastRefreshTime,
  preloadedNotifications = [],
  shouldFetchData = true,
  onNotificationClick,
  onNotificationAction,
  onBeforeRedirect,
  onUnreadCountChange,
  onRefreshComplete,
}) => {
  const navigate = useNavigate();
  const [isLoading, error, sendRequest] = useHttp();
  const dispatch = useDispatch();
  const theme = useTheme();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const isLightTheme = !darkMode;

  const { notifications: reduxNotifications, initialized: reduxInitialized } = useSelector(
    (state: RootState) => state.notificationAction
  );
  const { unreadCount, readCount, totalCount } = useSelector(selectNotificationCounts);

  const [notifications, setNotifications] = useState<NotificationInterface[]>([]);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [showReadNotifications, setShowReadNotifications] = useState<boolean>(false);
  const [lastInternalRefresh, setLastInternalRefresh] = useState<Date>(new Date());
  const [markingAsRead, setMarkingAsRead] = useState<Set<string>>(new Set());
  const [initialized, setInitialized] = useState<boolean>(false);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [filterType, setFilterType] = useState<'all' | 'unread' | 'read'>('unread');

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const mountedRef = useRef<boolean>(true);
  const forceRefreshRef = useRef<boolean>(forceRefresh);
  const fetchingRef = useRef<boolean>(false);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const getHeaders = () => {
    const headers: { header: string; value: string }[] = [];
    if (userToken) {
      headers.push({ header: "Authorization", value: `Bearer ${userToken}` });
    }
    return headers;
  };

  const updateStatesAfterFetch = useCallback(
    (fetchedNotifications: NotificationInterface[]) => {
      if (!mountedRef.current) return;

      const safeNotifications = Array.isArray(fetchedNotifications) ? fetchedNotifications : [];

      setNotifications(safeNotifications);
      setLastInternalRefresh(new Date());
      setInitialized(true);
      onUnreadCountChange?.(unreadCount);
      onRefreshComplete?.();
    },
    [onUnreadCountChange, onRefreshComplete, unreadCount]
  );

  const fetchNotifications = async () => {
    if (!mountedRef.current || fetchingRef.current) return;

    fetchingRef.current = true;

    try {
      const queryParams = new URLSearchParams({
        limit: String(maxNotifications),
        page: "1",
      });
      const url = `${CoreAPI_URLs.NOTIFICATIONS_GET_ALL}?${queryParams}`;

      const handleNotificationsSuccess = (results: any) => {
        if (!mountedRef.current) return;
        let fetchedNotifications: NotificationInterface[] = [];

        if (results && Array.isArray(results.results)) {
          fetchedNotifications = results.results;
        } else {
          fetchedNotifications = [];
        }
        dispatch(notificationActions.setNotifications(fetchedNotifications));
        updateStatesAfterFetch(fetchedNotifications);
      };

      const response = await sendRequest<NotificationInterface>(
        url,
        handleNotificationsSuccess,
        "GET",
        undefined,
        getHeaders()
      );
      if (!response) {
        console.error("[NotificationWidget] Request failed, useHttp handled the error");
      }
    } finally {
      fetchingRef.current = false;
    }
  };

  useEffect(() => {
    if (initialized) return;

    if (!shouldFetchData) {
      const notifications = reduxNotifications || [];
      setNotifications(notifications);
      setInitialized(true);
      if (!reduxInitialized) {
        dispatch(notificationActions.setInitialized(true));
      }

      return;
    }
    if (preloadedNotifications && preloadedNotifications.length > 0) {
      dispatch(notificationActions.setNotifications(preloadedNotifications));
      updateStatesAfterFetch(preloadedNotifications);
    } else {
      fetchNotifications();
    }
  }, [
    initialized,
    shouldFetchData,
    reduxNotifications,
    reduxInitialized,
    preloadedNotifications,
    updateStatesAfterFetch,
    fetchNotifications,
    dispatch,
  ]);

  useEffect(() => {
    if (!shouldFetchData && reduxInitialized && reduxNotifications) {
      setNotifications(reduxNotifications);
      if (!initialized) {
        setInitialized(true);
      }
    }
  }, [shouldFetchData, reduxInitialized, reduxNotifications, initialized]);

  useEffect(() => {
    if (initialized) {
      onUnreadCountChange?.(unreadCount);
    }
  }, [unreadCount, initialized, onUnreadCountChange]);

  useEffect(() => {
    if (!initialized || !forceRefresh) return;
    if (forceRefreshRef.current === forceRefresh) return;

    forceRefreshRef.current = forceRefresh;
    fetchNotifications();
  }, [forceRefresh, initialized, fetchNotifications]);

  useEffect(() => {
    if (!autoRefresh || !initialized) return;
    if (preloadedNotifications && preloadedNotifications.length > 0) return;
    intervalRef.current = setInterval(() => {
      if (mountedRef.current && !fetchingRef.current) {
        fetchNotifications();
      }
    }, refreshInterval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [autoRefresh, initialized, refreshInterval, fetchNotifications, preloadedNotifications]);

  useEffect(() => {
    if (initialized && notifications.length > 0) {
      setIsVisible(true);
    }
  }, [initialized, notifications.length]);

  const getNotificationIcon = (type: NotificationType) => {
    switch (type) {
      case NotificationType.NEW_APPOINTMENT:
      case NotificationType.APPOINTMENT_UPDATE:
      case NotificationType.APPOINTMENT_CANCELLED:
        return <EventIcon />;
      case NotificationType.PAYMENT:
        return <PaymentIcon />;
      case NotificationType.REMINDER:
        return <ReminderIcon />;
      case NotificationType.PURCHASE:
        return <PurchaseIcon />;
      case NotificationType.VIDEOCHAT:
        return <Videocam />;
      case NotificationType.CLAIM_NOT_SENT:
        return <ReportProblem />;
      case NotificationType.DEBT:
        return <AccountBalanceWallet />;
      case NotificationType.GENERAL:
        return <InfoIcon />;
      default:
        return <NotificationIcon />;
    }
  };


  const getPriorityColor = (priority: NotificationPriority) => {
    switch (priority) {
      case NotificationPriority.URGENT:
        return "error";
      case NotificationPriority.HIGH:
        return "warning";
      case NotificationPriority.MEDIUM:
        return "info";
      case NotificationPriority.LOW:
        return "success";
      default:
        return "default";
    }
  };

  const getPriorityIcon = (priority: NotificationPriority) => {
    switch (priority) {
      case NotificationPriority.URGENT:
        return <ErrorIcon fontSize="small" />;
      case NotificationPriority.HIGH:
        return <WarningIcon fontSize="small" />;
      case NotificationPriority.MEDIUM:
        return <InfoIcon fontSize="small" />;
      case NotificationPriority.LOW:
        return <CheckCircleIcon fontSize="small" />;
      default:
        return <InfoIcon fontSize="small" />;
    }
  };

  const formatLastRefreshTime = (date: Date): string => {
    try {
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMinutes = Math.floor(diffMs / (1000 * 60));

      if (diffMinutes < 1) {
        return t("notificationWidget.now");
      } else if (diffMinutes < 60) {
        return `${diffMinutes} ${t("notificationWidget.minutesAgo")}`;
      } else {
        return date.toLocaleTimeString("es-MX", {
          hour: "2-digit",
          minute: "2-digit",
        });
      }
    } catch (e) {
      return t("notificationWidget.invalidHour");
    }
  };

  const handleMarkAsRead = async (notificationId: string) => {
    if (markingAsRead.has(notificationId)) return;

    try {
      setMarkingAsRead((prev) => new Set(prev).add(notificationId));

      const url = `${CoreAPI_URLs.NOTIFICATIONS_MARK_AS_READ}`;
      const resp = await sendRequest(
        url,
        (data) => {
          dispatch(notificationActions.markAsRead(notificationId));

          setNotifications((prev) =>
            prev.map((n) => (n.id === notificationId ? { ...n, isRead: true } : n))
          );
          onNotificationAction?.("mark_read", notificationId);

          setSnackbarMessage(t("notificationWidget.notificationMarkedAsRead"));
          setSnackbarOpen(true);
        },
        "PUT",
        JSON.stringify({ notificationIds: [notificationId] }),
        getHeaders()
      );
    } finally {
      setMarkingAsRead((prev) => {
        const newSet = new Set(prev);
        newSet.delete(notificationId);
        return newSet;
      });
    }
  };

  const handleMarkAllAsRead = async () => {
    const unreadNotifications = notifications.filter((n) => n && !n.isRead);
    const unreadIds = unreadNotifications.map((n) => n.id);

    if (unreadIds.length === 0) {
      setSnackbarMessage(t("notificationWidget.noUnreadNotifications"));
      setSnackbarOpen(true);
      return;
    }

    try {
      const url = `${CoreAPI_URLs.NOTIFICATIONS_MARK_AS_READ}`;
      await sendRequest(
        url,
        (data) => {
          dispatch(notificationActions.markMultipleAsRead(unreadIds));

          setNotifications((prev) =>
            prev.map((n) => (unreadIds.includes(n.id) ? { ...n, isRead: true } : n))
          );
          onNotificationAction?.("mark_all_read", "");

          setSnackbarMessage(
            `${unreadIds.length} ${t("notificationWidget.notificationsMarkedAsRead")}`
          );
          setSnackbarOpen(true);
        },
        "PUT",
        JSON.stringify({ notificationIds: unreadIds }),
        getHeaders()
      );
    } catch (err) {
      console.error("Error marking all as read:", err);
    }
  };

  const handleNotificationClick = (notification: NotificationInterface) => {
    onNotificationClick?.(notification);

    if (!notification.isRead) {
      handleMarkAsRead(notification.id);
    }

      // If the notification type is PAST_APPOINTMENTS_ARRIVED, navigate to /appointments and open the modal 
    if (notification.type === NotificationType.PAST_APPOINTMENTS_ARRIVED) {
      navigate('/appointments', { state: { showPastAppointmentsModal: true } });
      return;
    }

    if (enableRedirect && notification.link) {
      if (onBeforeRedirect && !onBeforeRedirect(notification)) {
        return;
      }

      if (notification.link.startsWith("http")) {
        window.open(notification.link, "_blank");
      } else {
        navigate(notification.link);
      }
    }
  };

  const handleRefresh = () => {
    if (!fetchingRef.current) {
      fetchNotifications();
    }
  };

  const handleToggleReadNotifications = () => {
    if (filterType === 'unread') {
      setFilterType('all');
    } else {
      setFilterType('unread');
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const safeNotifications = Array.isArray(notifications) ? notifications : [];
  const filteredNotifications = filterType === 'all'
    ? safeNotifications
    : filterType === 'unread'
    ? safeNotifications.filter((n) => n && !n.isRead)
    : safeNotifications.filter((n) => n && n.isRead);

  const totalNotifications = totalCount;
  const unreadNotifications = unreadCount;
  const readNotifications = readCount;

  const renderNotificationItem = (notification: NotificationInterface) => {
    if (!notification) return null;

    return (
      <ListItem
        key={notification.id}
        className={`notification-widget-item${notification.isRead ? ' notification-widget-item--read' : ' notification-widget-item--unread'}`}
        onClick={() => handleNotificationClick(notification)}
      >
        <ListItemIcon>
          <Badge
            color={getPriorityColor(notification.priority) as any}
            variant="dot"
            invisible={notification.isRead}
          >
            {getNotificationIcon(notification.type)}
          </Badge>
        </ListItemIcon>

        <ListItemText
          primary={
            <Box display="flex" alignItems="center" gap={1}>
              <Typography
                variant="subtitle2"
                className={`notification-widget-title${notification.isRead ? ' notification-widget-title--read' : ' notification-widget-title--unread'}`}
              >
                {notification.title}
              </Typography>
              <Chip
                icon={getPriorityIcon(notification.priority)}
                label={notification.priority}
                size="small"
                color={getPriorityColor(notification.priority) as any}
                variant="outlined"
              />
            </Box>
          }
          secondary={
            <Box>
              <Typography variant="body2" className="notification-widget-message">
                {notification.message}
              </Typography>
              <Typography variant="caption" className="notification-widget-date">
                {formatDateMessages(notification.createdAt?.toString() || "")}
              </Typography>
            </Box>
          }
        />

        <Box display="flex" flexDirection="column" gap={0.5}>
          {!notification.isRead && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleMarkAsRead(notification.id);
              }}
              disabled={markingAsRead.has(notification.id)}
              title={t("notificationWidget.markAsRead")}
            >
              {markingAsRead.has(notification.id) ? (
                <CircularProgress size={16} />
              ) : (
                <MarkReadIcon fontSize="small" />
              )}
            </IconButton>
          )}

          {notification.link && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                if (notification.link?.startsWith("http")) {
                  window.open(notification.link, "_blank");
                } else if (notification.link) {
                  navigate(notification.link);
                }
              }}
              title={t("notificationWidget.openLink")}
            >
              <LaunchIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </ListItem>
    );
  };

  const renderHeader = () => (
    <Box
      className={`notification-widget-header${isLightTheme ? ' notification-widget-header--light' : ' notification-widget-header--dark'}`}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Typography
          variant="body2"
          className={`notification-widget-header-title${isLightTheme ? ' notification-widget-header-title--light' : ''}`}
        >
          {t("notificationWidget.title")}
        </Typography>

        <Box display="flex" alignItems="center" gap={1}>
          {shouldFetchData && (
            <IconButton
              size="small"
              onClick={handleRefresh}
              disabled={isLoading || fetchingRef.current}
              title="Actualizar"
            >
              {isLoading || fetchingRef.current ? (
                <CircularProgress className="notification-widget-logo" size={16} />
              ) : (
                <RefreshIcon className="notification-widget-logo" />
              )}
            </IconButton>
          )}

          <Badge
            badgeContent={unreadCount}
            color="error"
            className={`notification-widget-header-badge${isLightTheme ? ' notification-widget-header-badge--light' : ''}`}
          >
            <NotificationIcon className={`notification-widget-header-icon${isLightTheme ? ' notification-widget-header-icon--light' : ''}`} />
          </Badge>

          {unreadCount > 0 && (
            <Button
              size="small"
              onClick={handleMarkAllAsRead}
              disabled={isLoading || fetchingRef.current}
              startIcon={<MarkReadIcon className="notification-widget-logo" />}
              className={`notification-widget-header-button${isLightTheme ? ' notification-widget-header-button--light' : ' notification-widget-header-button--dark'}`}
              variant="outlined"
            >
              {t("notificationWidget.markAllAsRead")}
            </Button>
          )}
        </Box>
      </Box>

      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography
          variant="body2"
          className={`notification-widget-header-caption${isLightTheme ? ' notification-widget-header-caption--light' : ' notification-widget-header-caption--dark'}`}
        >
          {shouldFetchData
            ? `${t("notificationWidget.lastRefresh")}: ${formatLastRefreshTime(lastRefreshTime || lastInternalRefresh)}`
            : `${t("notificationWidget.total")}: (${safeNotifications.length} ${t("notificationWidget.notifications")})`}
        </Typography>

        <Button
          size="small"
          onClick={handleToggleReadNotifications}
          startIcon={
            filterType === 'unread' ? (
              <ExpandMoreIcon className="notification-widget-logo" />
            ) : (
              <HideIcon className="notification-widget-logo" />
            )
          }
          className="notification-widget-toggle-read"
        >
          {filterType === 'unread'
            ? t("notificationWidget.showRead")
            : t("notificationWidget.hideRead")}
        </Button>
      </Box>

      {showStats && (
        <Box className="notification-widget-stats-box">
          <Chip
            size="small"
            label={`${t("notificationWidget.total")}: ${totalNotifications}`}
            onClick={() => setFilterType('all')}
            className={`notification-widget-stats-chip${isLightTheme ? ' notification-widget-stats-chip--total-light' : ' notification-widget-stats-chip--total-dark'}${filterType === 'all' ? ' notification-widget-stats-chip--active' : ''}`}
            clickable
          />
          <Chip
            size="small"
            label={`${t("notificationWidget.unread")}: ${unreadNotifications}`}
            onClick={() => setFilterType('unread')}
            className={`notification-widget-stats-chip${isLightTheme ? ' notification-widget-stats-chip--unread-light' : ' notification-widget-stats-chip--unread-dark'}${filterType === 'unread' ? ' notification-widget-stats-chip--active' : ''}`}
            clickable
          />
          <Chip
            size="small"
            label={`${t("notificationWidget.read")}: ${readNotifications}`}
            onClick={() => setFilterType('read')}
            className={`notification-widget-stats-chip${isLightTheme ? ' notification-widget-stats-chip--read-light' : ' notification-widget-stats-chip--read-dark'}${filterType === 'read' ? ' notification-widget-stats-chip--active' : ''}`}
            clickable
          />
        </Box>
      )}
    </Box>
  );

  const renderContent = () => {
    if ((isLoading || fetchingRef.current) && safeNotifications.length === 0) {
      return (
        <Box className="notification-widget-loading">
          <CircularProgress />
          <Typography variant="body2" className="notification-widget-loading-text">
            {t("notificationWidget.loading")}
          </Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Box className="notification-widget-error">
          <Alert
            severity="error"
            action={
              shouldFetchData && (
                <Button size="small" onClick={handleRefresh}>
                  {t("notificationWidget.refresh")}
                </Button>
              )
            }
          >
            {error}
          </Alert>
        </Box>
      );
    }

    if (filteredNotifications.length === 0) {
      return (
        <Box className="notification-widget-empty">
          <NotificationIcon className="notification-widget-logo notification-widget-empty-icon" />
          <Typography variant="body2" className="notification-widget-empty-text" align="center">
            {safeNotifications.length === 0
              ? t("notificationWidget.noNotifications")
              : filterType === 'unread'
                ? t("notificationWidget.noUnreadNotifications")
                : filterType === 'read'
                ? t("notificationWidget.noReadNotifications")
                : t("notificationWidget.noNotifications")}
          </Typography>
        </Box>
      );
    }

    const unreadNotifs = filteredNotifications.filter(n => !n.isRead);
    const readNotifs = filteredNotifications.filter(n => n.isRead);

    return (
      <List className="notification-widget-list">
        {filterType === 'all' && readNotifs.length > 0 && (
          <>
            <>
              <Divider className="notification-widget-divider notification-widget-divider--read" />
              <Chip 
                label={`${t("notificationWidget.read")} (${readNotifs.length})`} 
                size="small"
                className={`notification-widget-chip notification-widget-chip--read notification-widget-divider-chip${isLightTheme ? ' MuiChip-label--light' : ' MuiChip-label--dark'}`}
              />
            </>
            {readNotifs.map(renderNotificationItem)}
          </>
        )}
        
        {filterType === 'all' && unreadNotifs.length > 0 && readNotifs.length > 0 && (
          <>
            <Divider className="notification-widget-divider notification-widget-divider--unread" />
            <Chip 
              label={`${t("notificationWidget.unread")} (${unreadNotifs.length})`} 
              size="small"
              className={`notification-widget-chip notification-widget-chip--unread notification-widget-divider-chip${isLightTheme ? ' MuiChip-label--light' : ' MuiChip-label--dark'}`}
            />
          </>
        )}
        
        {(filterType === 'all' || filterType === 'unread') && unreadNotifs.map(renderNotificationItem)}
        {filterType === 'read' && readNotifs.map(renderNotificationItem)}
      </List>
    );
  };

  if (!initialized && !isLoading && !reduxInitialized) {
    return (
      <Box className="notification-widget-loading">
        <CircularProgress />
        <Typography variant="body2" className="notification-widget-loading-text">
          {t("notificationWidget.initializing")}
        </Typography>
      </Box>
    );
  }

  return (
    <Card
      className={`notification-widget${isLightTheme ? ' notification-widget--light' : ' notification-widget--dark'}`}
    >
      {renderHeader()}
      <Box className="notification-widget-content">{renderContent()}</Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message={snackbarMessage}
        className="notification-widget-snackbar"
      />
    </Card>
  );
};

export default NotificationWidget;
